import { useOracleAutonomy } from "@/hooks/useOracleAutonomy";
import { formatNsAge } from "@/lib/oracle/oracle-autonomy";

function Pill({
	ok,
	label,
}: {
	ok: boolean | null | undefined;
	label: string;
}) {
	const known = ok != null;
	const cls = !known
		? "bg-muted text-muted-foreground"
		: ok
			? "bg-primary/15 text-primary"
			: "bg-danger/15 text-danger";
	return (
		<span
			className={`rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide ${cls}`}
		>
			{label}
			{known ? (ok ? " · on" : " · off") : " · n/a"}
		</span>
	);
}

/** On-chain auto-refresh + protocol pull status (live queries). */
export function AutonomyStatusPanel() {
	const { data, isLoading, isError, dataUpdatedAt, refetch, isFetching } =
		useOracleAutonomy();
	const r = data?.refresh;
	const a = data?.automation;

	return (
		<section className="card-surface space-y-4 p-4 sm:p-5">
			<div className="flex flex-wrap items-start justify-between gap-3">
				<div>
					<p className="section-eyebrow">Autonomy</p>
					<h2 className="text-lg font-bold">On-chain price refresh</h2>
					<p className="mt-1 max-w-2xl text-sm text-muted-foreground">
						Autonomous path: oracle canister timer calls XRC; protocol timer
						pulls those marks. Host multi-API keeper only when XRC is degraded.
					</p>
				</div>
				<button
					type="button"
					className="rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted/40 disabled:opacity-50"
					disabled={isFetching}
					onClick={() => void refetch()}
				>
					{isFetching ? "Refreshing…" : "Refresh status"}
				</button>
			</div>

			{isLoading ? (
				<div className="skeleton-shimmer h-24 rounded-xl" />
			) : isError && !r && !a ? (
				<p className="text-sm text-danger">
					Could not load autonomy status (missing oracle env or old wasm).
				</p>
			) : (
				<>
					<div className="flex flex-wrap gap-2">
						<Pill ok={r?.timerArmed} label="Oracle timer" />
						<Pill ok={r?.autoRefreshEnabled} label="Auto-refresh" />
						<Pill
							ok={r != null ? !r.xrcDegraded : null}
							label={r?.xrcDegraded ? "XRC degraded" : "XRC healthy"}
						/>
						<Pill ok={a?.oracleSyncTimerArmed} label="Protocol sync timer" />
						<Pill ok={a?.oracleFresh} label="Oracle fresh" />
					</div>

					<div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
						<div className="rounded-xl border border-border/60 bg-muted/20 px-3 py-2">
							<p className="text-[10px] font-semibold uppercase text-muted-foreground">
								Refresh interval
							</p>
							<p className="mt-1 font-mono-amount text-sm font-semibold">
								{r
									? `${r.refreshIntervalSecs.toString()}s` +
										(r.effectiveIntervalSecs !== r.refreshIntervalSecs
											? ` (eff ${r.effectiveIntervalSecs.toString()}s)`
											: "")
									: "—"}
							</p>
						</div>
						<div className="rounded-xl border border-border/60 bg-muted/20 px-3 py-2">
							<p className="text-[10px] font-semibold uppercase text-muted-foreground">
								Protocol sync interval
							</p>
							<p className="mt-1 font-mono-amount text-sm font-semibold">
								{a?.oracleSyncIntervalSecs != null
									? `${a.oracleSyncIntervalSecs.toString()}s`
									: "—"}
							</p>
						</div>
						<div className="rounded-xl border border-border/60 bg-muted/20 px-3 py-2">
							<p className="text-[10px] font-semibold uppercase text-muted-foreground">
								Last protocol sync
							</p>
							<p className="mt-1 font-mono-amount text-sm font-semibold">
								{formatNsAge(a?.lastOracleSyncNs)}
							</p>
						</div>
						<div className="rounded-xl border border-border/60 bg-muted/20 px-3 py-2">
							<p className="text-[10px] font-semibold uppercase text-muted-foreground">
								XRC consecutive fails (max)
							</p>
							<p className="mt-1 font-mono-amount text-sm font-semibold">
								{r ? r.maxConsecutiveFails.toString() : "—"}
							</p>
						</div>
					</div>

					{r && r.assets.length > 0 ? (
						<div className="overflow-x-auto">
							<table className="w-full min-w-[28rem] text-left text-xs">
								<thead>
									<tr className="border-b border-border text-muted-foreground">
										<th className="py-2 pr-3 font-medium">Asset</th>
										<th className="py-2 pr-3 font-medium">Last XRC success</th>
										<th className="py-2 pr-3 font-medium">Fails</th>
										<th className="py-2 font-medium">Last error</th>
									</tr>
								</thead>
								<tbody>
									{r.assets.map((row) => (
										<tr
											key={row.asset}
											className="border-b border-border/50 last:border-0"
										>
											<td className="py-2 pr-3 font-semibold">{row.asset}</td>
											<td className="py-2 pr-3 font-mono-amount">
												{formatNsAge(row.lastSuccessNs)}
											</td>
											<td className="py-2 pr-3 font-mono-amount">
												{row.consecutiveFails.toString()}
											</td>
											<td
												className="max-w-[14rem] truncate py-2 text-muted-foreground"
												title={row.lastError || undefined}
											>
												{row.lastError || "—"}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					) : null}

					{dataUpdatedAt ? (
						<p className="text-[10px] text-muted-foreground">
							Status polled{" "}
							{new Date(dataUpdatedAt).toLocaleTimeString()} · every ~45s
						</p>
					) : null}
				</>
			)}
		</section>
	);
}
